import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
} from '@heroicons/react/20/solid';

import { useTranslation } from 'react-i18next';
import { PiUsersLight, PiMapPinLight, PiTagLight, PiPlusLight } from 'react-icons/pi';
import Header, { Action } from '../shared/TailwindComponents/Header';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { EVENT_TYPE_COLORS, CalendarEventType } from '../Calendar/types';

dayjs.extend(isBetween);
dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

/** Parse backend ISO timestamp as UTC and return as local dayjs.
 *  Backend sometimes omits the Z/offset suffix; without explicit UTC parsing
 *  dayjs treats it as local and dates shift. */
function parseEventDate(timestamp: any): dayjs.Dayjs {
  if (!timestamp) return dayjs();
  const trimmed = String(timestamp).trim();
  const hasTz = /Z$/i.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed);
  return hasTz ? dayjs(trimmed) : dayjs.utc(trimmed).local();
}

/** Get Tailwind color classes for a given event_type, falling back to blue */
function getEventColors(eventType?: string) {
  const key = (eventType || 'custom') as CalendarEventType;
  return EVENT_TYPE_COLORS[key] ?? EVENT_TYPE_COLORS.custom;
}

interface CalendarViewProps {
  tasks: any[];
  onRangeChange: (start: Date, end: Date) => void;
  onSelectEvent: (event: any) => void;
  onEventDrop?: (eventId: string, newDeadline: Date) => void;
  calendarViewMode: 'day' | 'week' | 'month' | 'year';
  onCalendarViewModeChange: (mode: 'day' | 'week' | 'month' | 'year') => void;
  onAddTask?: () => void;
  currentDate: dayjs.Dayjs;
  onDateChange: (newDate: dayjs.Dayjs) => void;
  isDayLoading?: boolean;
  extraActions?: Action[];
  filterBar?: React.ReactNode;
}

function classNames(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

const SkeletonEvent = () => (
  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded animate-fadePulse w-3/4" />
);

const EventTooltip = ({ event }: { event: any }) => (
  <div className="w-80 px-5 py-4 bg-white dark:bg-gray-900 shadow-2xl rounded-xl border border-gray-100 dark:border-gray-800 backdrop-blur-sm transition-all duration-150 ease-in-out">
    {/* Header */}
    <div className="flex items-start gap-3 mb-3">
      <div className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
        <ClockIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold text-gray-900 dark:text-white truncate text-sm leading-tight">{event.title}</h3>
        {event.start_time && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {parseEventDate(event.start_time).format('h:mm A, ddd MMM D')}
          </p>
        )}
      </div>
    </div>

    {/* Description */}
    {event.description && (
      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-3 leading-relaxed mb-3">{event.description}</p>
    )}

    {/* Location */}
    {event.location && (
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3">
        <PiMapPinLight className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate">{event.location}</span>
      </div>
    )}

    {/* Shared with */}
    {(event.shared_with?.length || 0) > 0 && (
      <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-1.5 mb-1.5">
          <PiUsersLight className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">{event.shared_with.length} shared</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {event.shared_with.slice(0, 5).map((collab: any, i: number) => (
            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 truncate max-w-[120px]">
              {collab.Name}
            </span>
          ))}
          {event.shared_with.length > 5 && (
            <span className="text-[10px] text-gray-400">+{event.shared_with.length - 5}</span>
          )}
        </div>
      </div>
    )}

    {/* Tags */}
    {(event.tags?.length || 0) > 0 && (
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex flex-wrap gap-1">
        {event.tags.map((tag: string) => (
          <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 ring-1 ring-inset ring-indigo-200 dark:ring-indigo-800">
            <PiTagLight className="h-2.5 w-2.5 mr-0.5 flex-shrink-0" />
            {tag}
          </span>
        ))}
      </div>
    )}
  </div>
);

const EventItem = ({ event, onSelect }: { event: any; onSelect: (event: any) => void }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const itemRef = React.useRef<HTMLLIElement>(null);
  const [tooltipStyle, setTooltipStyle] = React.useState<React.CSSProperties>({});

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsHovered(true);

    // Calculate tooltip position to avoid viewport clipping
    if (itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      const tooltipWidth = 320;
      const tooltipHeight = 250;
      const padding = 12;

      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      let top = rect.bottom + 8;

      // Clamp horizontal
      if (left < padding) left = padding;
      if (left + tooltipWidth > window.innerWidth - padding) left = window.innerWidth - padding - tooltipWidth;

      // Flip to above if below viewport
      if (top + tooltipHeight > window.innerHeight - padding) {
        top = rect.top - tooltipHeight - 8;
      }

      setTooltipStyle({ position: 'fixed', left, top, zIndex: 9999 });
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsHovered(false), 300);
  };

  return (
    <li ref={itemRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        onClick={() => onSelect(event)}
        className="flex w-full items-center text-left text-xs focus:outline-none group hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-md px-1 py-0.5 transition-colors duration-100"
      >
        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 flex-shrink-0 ${getEventColors(event.event_type).dot}`} />
        <p className="flex-auto truncate font-medium text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400">
          {event.title}
        </p>
        <time
          dateTime={event.start_time || ''}
          className="ml-3 hidden flex-none text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 xl:block"
        >
          {event.start_time ? parseEventDate(event.start_time).format('hA') : ''}
        </time>
      </button>
      {isHovered && ReactDOM.createPortal(
        <div style={tooltipStyle}>
          <EventTooltip event={event} />
        </div>,
        document.body
      )}
    </li>
  );
};

const CalendarView: React.FC<CalendarViewProps> = ({
  calendarViewMode,
  onCalendarViewModeChange,
  tasks,
  onRangeChange,
  onSelectEvent,
  onEventDrop,
  onAddTask,
  onDateChange,
  currentDate,
  isDayLoading = false,
  extraActions,
  filterBar,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerNavRef = useRef<HTMLDivElement | null>(null);
  const containerOffsetRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation()
  const { setBreadcrumbs } = useBreadcrumbs();

  const updateBreadcrumbs = () => {
    const viewDetail = calendarViewMode === 'day' ? currentDate.format('MMMM D, YYYY') :
      calendarViewMode === 'week' ? `${currentDate.startOf('week').format('MMM D')} - ${currentDate.endOf('week').format('MMM D, YYYY')}` :
        calendarViewMode === 'month' ? currentDate.format('MMMM YYYY') :
          currentDate.format('YYYY');

    setBreadcrumbs([
      { name: t('Calendar'), href: '/calendar', current: false },
      { name: viewDetail, href: '#', current: true },
    ]);
  };

  useEffect(() => {
    updateBreadcrumbs();
  }, [calendarViewMode, currentDate]);

  useEffect(() => {
    updateBreadcrumbs();
  }, []);

  const getRange = useCallback(() => {
    let start: dayjs.Dayjs;
    let end: dayjs.Dayjs;

    if (calendarViewMode === 'month') {
      start = currentDate.startOf('month').startOf('week');
      end = currentDate.endOf('month').endOf('week');
    } else if (calendarViewMode === 'week') {
      start = currentDate.startOf('week');
      end = currentDate.endOf('week');
    } else if (calendarViewMode === 'day') {
      start = currentDate.startOf('day');
      end = currentDate.endOf('day');
    } else {
      start = currentDate.startOf('year');
      end = currentDate.endOf('year');
    }
    return { start, end };
  }, [calendarViewMode, currentDate]);

  useEffect(() => {
    const { start, end } = getRange();
    onRangeChange(start.toDate(), end.toDate());
  }, [getRange, onRangeChange]);

  const eventsByDate = useMemo(() => {
    const { start, end } = getRange();
    const map: Record<string, any[]> = {};

    let temp = start.clone();
    while (temp.isBefore(end) || temp.isSame(end, 'day')) {
      map[temp.format('YYYY-MM-DD')] = [];
      temp = temp.add(1, 'day');
    }

    if (!isDayLoading) {
      tasks.forEach(task => {
        if (task.start_time) {
          const deadline = parseEventDate(task.start_time);
          if (deadline.isBetween(start, end, null, '[]')) {
            const key = deadline.format('YYYY-MM-DD');
            map[key].push(task);
          }
        }
      });
    }

    return map;
  }, [tasks, getRange, isDayLoading]);

  const daysArray = useMemo(() => {
    if (calendarViewMode === 'month' || calendarViewMode === 'week' || calendarViewMode === 'day') {
      const { start, end } = getRange();
      const days: {
        date: string;
        isCurrentMonth?: boolean;
        isToday?: boolean;
        isSelected?: boolean;
        events: any[];
      }[] = [];

      let temp = start.clone();
      while (temp.isBefore(end) || temp.isSame(end, 'day')) {
        const dStr = temp.format('YYYY-MM-DD');
        const isCurrentMonth = calendarViewMode !== 'day'
          ? (temp.month() === currentDate.month())
          : true;
        const isToday = temp.isSame(dayjs(), 'day');
        const isSelected = calendarViewMode === 'day' && temp.isSame(currentDate, 'day');

        days.push({
          date: dStr,
          isCurrentMonth,
          isToday,
          isSelected,
          events: eventsByDate[dStr] || []
        });

        temp = temp.add(1, 'day');
      }
      return days;
    } else {
      const yearStart = currentDate.startOf('year');
      const months = [];
      for (let i = 0; i < 12; i++) {
        const monthDate = yearStart.clone().month(i);
        months.push({
          date: monthDate.format('YYYY-MM-DD'),
          monthDate,
          isThisMonth: monthDate.isSame(dayjs(), 'month'),
          events: []
        });
      }
      return months;
    }
  }, [calendarViewMode, currentDate, eventsByDate, getRange]);


  const goToToday = () => onDateChange(dayjs());
  const goToNext = () => {
    if (calendarViewMode === 'month') onDateChange(currentDate.add(1, 'month'));
    else if (calendarViewMode === 'week') onDateChange(currentDate.add(1, 'week'));
    else if (calendarViewMode === 'day') onDateChange(currentDate.add(1, 'day'));
    else onDateChange(currentDate.add(1, 'year'));
  };
  const goToPrev = () => {
    if (calendarViewMode === 'month') onDateChange(currentDate.subtract(1, 'month'));
    else if (calendarViewMode === 'week') onDateChange(currentDate.subtract(1, 'week'));
    else if (calendarViewMode === 'day') onDateChange(currentDate.subtract(1, 'day'));
    else onDateChange(currentDate.subtract(1, 'year'));
  };

  const handleDayClick = (day: string) => {
    if (calendarViewMode === 'month' || calendarViewMode === 'week') {
      onDateChange(dayjs(day));
      onCalendarViewModeChange('day');
    }
  };

  const handleMonthClick = (monthDate: dayjs.Dayjs) => {
    if (calendarViewMode === 'year') {
      onDateChange(monthDate);
      onCalendarViewModeChange('month');
    }
  };

  let title = '';
  if (calendarViewMode === 'month') {
    title = currentDate.format('MMMM YYYY');
  } else if (calendarViewMode === 'week') {
    const start = currentDate.startOf('week').format('MMM D');
    const end = currentDate.endOf('week').format('MMM D, YYYY');
    title = `${start} - ${end}`;
  } else if (calendarViewMode === 'day') {
    title = currentDate.format('MMMM D, YYYY');
  } else {
    title = currentDate.format('YYYY');
  }

  const selectedDay =
    calendarViewMode === 'day' || calendarViewMode === 'month' || calendarViewMode === 'week'
      ? (daysArray as any[]).find((d) => d.isSelected)
      : null;

  useEffect(() => {
    if ((calendarViewMode === 'day' || calendarViewMode === 'week') && containerRef.current && containerNavRef.current && containerOffsetRef.current) {
      const currentMinute = new Date().getHours() * 60;
      const scrollHeight =
        containerRef.current.scrollHeight -
        containerNavRef.current.offsetHeight -
        containerOffsetRef.current.offsetHeight;

      containerRef.current.scrollTop = (scrollHeight * currentMinute) / 1440;
    }
  }, [calendarViewMode]);

  let monthRowCount = 6;
  if (calendarViewMode === 'month') {
    const totalDays = (daysArray as any[]).length;
    if (totalDays === 35) {
      monthRowCount = 5;
    }
  }

  const handleEventDragEnd = (eventId: string, newDateTime: Date) => {
    if (onEventDrop) onEventDrop(eventId, newDateTime);
  };

  const eventPositionForTime = (deadline: dayjs.Dayjs) => {
    const hour = deadline.hour();
    const minute = deadline.minute();
    const startRow = (hour * 12) + Math.floor(minute / 5) + 2;
    const spanRows = 6;
    return { startRow, spanRows };
  };
  const calendarActions: Action[] = [
    {
      label: t('Today'),
      onClick: goToToday,
      variant: 'neutral',
    },
  ];

  const modeDropdownActions: Action[] = [
    { label: t('Day'), onClick: () => onCalendarViewModeChange('day') },
    { label: t('Week'), onClick: () => onCalendarViewModeChange('week') },
    { label: t('Month'), onClick: () => onCalendarViewModeChange('month') },
    { label: t('Year'), onClick: () => onCalendarViewModeChange('year') },
  ];

  if (onAddTask) {
    calendarActions.push({
      label: t('Add Event'),
      onClick: onAddTask,
      variant: 'primary',
      icon: <PiPlusLight className="h-4 w-4" />,
    });
  }

  calendarActions.unshift(
    {
      label: t('Previous'),
      onClick: goToPrev,
      variant: 'outline',
      icon: <ChevronLeftIcon className="h-4 w-4" />,
    },
    {
      label: t('Next'),
      onClick: goToNext,
      variant: 'outline',
      icon: <ChevronRightIcon className="h-4 w-4" />,
    }
  );

  // Merge page-level actions into the header
  if (extraActions) {
    calendarActions.push(...extraActions);
  }

  // Current day events for day view sidebar
  const currentDayEvents = useMemo(() => {
    const key = currentDate.format('YYYY-MM-DD');
    return eventsByDate[key] || [];
  }, [currentDate, eventsByDate]);

  const renderDayEvents = (days: any[]) => {
    if (isDayLoading) {
      return days.map((day, dayIndex) => (
        <React.Fragment key={day.date}>
          {[...Array(2)].map((_, i) => {
            const startRow = 1 + i * 12;
            const spanRows = 6;
            return (
              <li
                key={i}
                className="relative mt-px flex"
                style={{ gridRow: `${startRow} / span ${spanRows}`, gridColumn: `${dayIndex + 1} / span 1` }}
              >
                <div className="group absolute inset-1 flex flex-col w-40 h-fit rounded-lg bg-gray-200 dark:bg-gray-600 p-2 animate-fadePulse" />
              </li>
            );
          })}
        </React.Fragment>
      ));
    }
    return days.map((day) => {
      return day.events.map((event: any) => {
        const deadline = parseEventDate(event.start_time);
        const { startRow, spanRows } = eventPositionForTime(deadline);
        const colors = getEventColors(event.event_type);

        return (
          <li
            key={event.id}
            className="relative mt-px flex overflow-visible"
            style={{ gridRow: `${startRow} / span ${spanRows}` }}
          >
            <button
              onClick={() => onSelectEvent(event)}
              className={`group absolute inset-1 flex flex-col overflow-hidden h-fit rounded-lg p-2 text-xs transition-all duration-100 border ${colors.bg} ${colors.darkBg} border-transparent hover:shadow-md hover:scale-[1.02]`}
            >
              <p className={`font-semibold truncate max-w-[90%] ${colors.text} ${colors.darkText}`}>
                {event.title}
              </p>
              <p className={`${colors.text} ${colors.darkText} opacity-70 mt-0.5`}>
                <time dateTime={event.start_time}>
                  {deadline.format('h:mm A')}
                </time>
              </p>
            </button>
          </li>
        );
      });
    });
  };

  return (
    <div className="
      lg:flex h-screen lg:flex-col 
        flex flex-col
      text-gray-900 dark:text-gray-100
    ">
      <div data-tour-id="calendar-header">
        <Header
          actions={calendarActions}
          dropdownActions={modeDropdownActions}
          dropdownLabel={t(calendarViewMode.charAt(0).toUpperCase() + calendarViewMode.slice(1))}
          dropdownIcon={<ChevronDownIcon className="h-4 w-4" />}
          renderDropdownAsButtons={false}
        />
      </div>

      {filterBar}

      <div className="
        flex-1 flex flex-col h-full overflow-hidden relative
        bg-white dark:bg-gray-800
        transition-colors duration-300
        transition-all duration-300 animate-fade-in-up
      ">
        <div data-tour-id="calendar-content" className="absolute top-0 left-0 right-0 h-[120px] pointer-events-none z-10" />
        {calendarViewMode === 'year' && (
          <div className="
            grid grid-cols-4 gap-4 p-4 h-full overflow-auto
            bg-white dark:bg-gray-800
          ">
            {(daysArray as any[]).map(m => {
              const monthDate = m.monthDate;
              return (
                <div
                  key={m.date}
                  onClick={() => handleMonthClick(monthDate)}
                  className={classNames(
                    'cursor-pointer p-4 rounded shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-center font-medium',
                    'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100',
                    m.isThisMonth && 'ring-2 ring-primary-500'
                  )}
                >
                  {monthDate.format('MMMM YYYY')}
                </div>
              );
            })}
          </div>
        )}

        {calendarViewMode === 'month' && (
          <div className="
            shadow ring-1 ring-black/5 lg:flex lg:flex-auto lg:flex-col h-full flex flex-col
            dark:shadow-none dark:ring-0
            bg-white dark:bg-gray-800
          ">
            <div className="
              grid grid-cols-7 gap-px border-b border-gray-300 dark:border-gray-600 
              bg-gray-200 dark:bg-gray-700 text-center text-xs font-semibold 
              text-gray-700 dark:text-gray-100 flex-none
            ">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map(d => (
                <div key={d} className="bg-white dark:bg-gray-800 py-2">{d}</div>
              ))}
            </div>
            <div className="flex-1 overflow-auto bg-gray-200 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-100">
              <div
                className={classNames(
                  `hidden w-full h-full lg:grid lg:grid-cols-7 lg:grid-rows-${monthRowCount} lg:gap-px`
                )}
              >
                {(daysArray as any[]).map(day => {
                  const d = dayjs(day.date);
                  return (
                    <div
                      key={day.date}
                      onClick={() => handleDayClick(day.date)}
                      className={classNames(
                        'relative px-3 py-2 cursor-pointer border border-gray-100 dark:border-gray-600',
                        day.isCurrentMonth
                          ? 'bg-white dark:bg-gray-800'
                          : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
                        'hover:bg-gray-100 dark:hover:bg-gray-600'
                      )}
                    >
                      <time
                        dateTime={day.date}
                        className={
                          day.isToday
                            ? 'flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 font-semibold text-white'
                            : ''
                        }
                      >
                        {d.date()}
                      </time>
                      {isDayLoading ? (
                        <div className="mt-2 space-y-1">
                          <SkeletonEvent />
                          <SkeletonEvent />
                        </div>
                      ) : day.events.length > 0 && (
                        <ol className="mt-2 space-y-1">
                          {day.events.slice(0, 2).map((event: any) => (
                            <EventItem key={event.id} event={event} onSelect={onSelectEvent} />
                          ))}
                          {day.events.length > 2 && (
                            <li className="text-gray-500 dark:text-gray-400 text-xs pt-1">
                              + {day.events.length - 2} more
                            </li>
                          )}
                        </ol>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {selectedDay && selectedDay.events.length > 0 && (
              <div className="
                px-4 py-10 sm:px-6 lg:hidden 
                bg-white dark:bg-gray-800
              ">
                <ol
                  className="
                    divide-y divide-gray-100 dark:divide-gray-600
                    overflow-hidden rounded-lg bg-white dark:bg-gray-700
                    text-sm shadow ring-1 ring-black/5 dark:ring-0
                  "
                >
                  {selectedDay.events.map((event: any) => (
                    <li key={event.id} className="group flex p-4 pr-6 hover:bg-gray-50 dark:hover:bg-gray-600">
                      <div className="flex-auto">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          {event.title}
                        </p>
                        {event.start_time && (
                          <time dateTime={event.start_time} className="mt-2 flex items-center text-gray-700 dark:text-gray-300">
                            <ClockIcon className="mr-2 h-5 w-5 text-gray-400 dark:text-gray-400" aria-hidden="true" />
                            {parseEventDate(event.start_time).format('h:mm A')}
                          </time>
                        )}
                      </div>
                      <button onClick={() => onSelectEvent(event)} className="ml-6 flex-none self-center rounded-md bg-white dark:bg-gray-600 px-3 py-2 font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-500 hover:ring-gray-400 dark:hover:ring-gray-400 group-hover:opacity-100">
                        Edit<span className="sr-only">, {event.title}</span>
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {calendarViewMode === 'week' && (
          <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-800">
            <div
              ref={containerRef}
              className="
                isolate flex flex-auto flex-col overflow-auto 
                relative bg-white dark:bg-gray-800
              "
            >
              <div
                ref={containerNavRef}
                className="
                  sticky top-0 z-30 flex-none bg-white dark:bg-gray-800 
                  shadow ring-1 ring-black/5 dark:ring-gray-700 sm:pr-8
                "
              >
                <div className="grid grid-cols-7 text-sm text-gray-500 sm:hidden">
                  {(daysArray as any[]).map((day, i) => {
                    const d = dayjs(day.date);
                    return (
                      <button key={day.date} type="button" className="flex flex-col items-center pb-3 pt-2 border-r border-gray-200 dark:border-gray-700">
                        {d.format('dd').charAt(0)} <span className={classNames(
                          "mt-1 flex size-8 items-center justify-center font-semibold",
                          day.isToday ? 'rounded-full bg-primary-500 text-white' : 'text-gray-900 dark:text-gray-100'
                        )}>{d.date()}</span>
                      </button>
                    )
                  })}
                </div>
                <div className="-mr-px hidden grid-cols-7 divide-x divide-gray-200 dark:divide-gray-700 border-r border-gray-200 dark:border-gray-700 text-sm text-gray-500 sm:grid">
                  <div className="col-end-1 w-14" />
                  {(daysArray as any[]).map((day, i) => {
                    const d = dayjs(day.date);
                    return (
                      <div key={day.date} className="flex items-center justify-center py-3">
                        <span className="flex items-baseline">
                          <span className='dark:text-gray-500'>{d.format('ddd')}</span> <span className={classNames(
                            "items-center dark:text-white justify-center font-semibold ml-1.5",
                            day.isToday ? 'rounded-full bg-primary-500 text-white px-2' : 'text-gray-900'
                          )}>{d.date()}</span>
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="flex flex-auto h-full overflow-auto">
                <div
                  className="sticky left-0 z-10 w-14 flex-none bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700"
                />
                <div className="grid flex-auto grid-cols-7 grid-rows-1">
                  <div
                    className="col-start-1 col-end-8 row-start-1 grid divide-y divide-gray-200 dark:divide-gray-700"
                    style={{ gridTemplateRows: 'repeat(48, minmax(3.5rem, 1fr))' }}
                  >
                    <div ref={containerOffsetRef} className="row-end-1 h-7"></div>
                    {[...Array(24)].map((_, hour) => (
                      <React.Fragment key={hour}>
                        <div>
                          <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs text-gray-400 dark:text-gray-500">
                            {hour === 0 ? '12AM' : hour < 12 ? `${hour}AM` : hour === 12 ? '12PM' : `${hour - 12}PM`}
                          </div>
                        </div>
                        <div className="border-b border-gray-200 dark:border-gray-700" />
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="col-start-1 col-end-8 row-start-1 grid grid-cols-7 divide-x divide-gray-200 dark:divide-gray-700">
                    <div className="col-start-1 row-span-full border-r border-gray-200 dark:border-gray-700" />
                    <div className="col-start-2 row-span-full border-r border-gray-200 dark:border-gray-700" />
                    <div className="col-start-3 row-span-full border-r border-gray-200 dark:border-gray-700" />
                    <div className="col-start-4 row-span-full border-r border-gray-200 dark:border-gray-700" />
                    <div className="col-start-5 row-span-full border-r border-gray-200 dark:border-gray-700" />
                    <div className="col-start-6 row-span-full border-r border-gray-200 dark:border-gray-700" />
                    <div className="col-start-7 row-span-full border-r border-gray-200 dark:border-gray-700" />
                  </div>
                  <ol
                    className="
                      col-start-1 col-end-8 row-start-1 grid grid-cols-7 pointer-events-auto sm:pr-8
                      bg-white dark:bg-gray-800
                    "
                    style={{ gridTemplateRows: '1.75rem repeat(288, minmax(0,1fr)) auto' }}
                  >
                    {(daysArray as any[]).map((day, dayIndex) => (
                      <React.Fragment key={day.date}>
                        {day.events.map((event: any) => {
                          const deadline = parseEventDate(event.start_time);
                          const { startRow, spanRows } = eventPositionForTime(deadline);
                          const colors = getEventColors(event.event_type);
                          return (
                            <li key={event.id} className="relative mt-px flex overflow-visible" style={{ gridRow: `${startRow} / span ${spanRows}`, gridColumn: `${dayIndex + 1} / span 1` }}>
                              <button
                                onClick={() => onSelectEvent(event)}
                                className={`group absolute inset-1 flex flex-col w-40 overflow-hidden h-fit rounded-lg p-2 text-xs border border-transparent hover:shadow-md hover:scale-[1.02] transition-all duration-100 ${colors.bg} ${colors.darkBg}`}
                              >
                                <p className={`font-semibold max-w-36 truncate ${colors.text} ${colors.darkText}`}>
                                  {event.title}
                                </p>
                                <p className={`${colors.text} ${colors.darkText} opacity-70 mt-0.5`}>
                                  <time dateTime={event.start_time}>
                                    {deadline.format('h:mm A')}
                                  </time>
                                </p>
                              </button>
                            </li>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}
        {calendarViewMode === 'day' && (
          <div className="flex flex-1 overflow-hidden bg-white dark:bg-gray-800">
            <div
              ref={containerRef}
              className="flex h-[100vh-200px] flex-auto flex-col overflow-auto relative"
            >
              <div
                ref={containerNavRef}
                className="
                  sticky top-0 z-10 grid flex-none grid-cols-7 
                  bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 shadow 
                  ring-1 ring-black/5 dark:ring-0 md:hidden
                "
              />
              <div className="flex w-full flex-auto">
                <div className="
                  w-14 flex-none bg-white dark:bg-gray-800 ring-1 ring-gray-100 dark:ring-gray-700
                " />
                <div className="grid flex-auto grid-cols-1 grid-rows-1 relative">
                  <div
                    className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100 dark:divide-gray-700"
                    style={{ gridTemplateRows: 'repeat(48, minmax(3.5rem, 1fr))' }}
                  >
                    <div ref={containerOffsetRef} className="row-end-1 h-7"></div>
                    {[...Array(24)].map((_, hour) => (
                      <React.Fragment key={hour}>
                        <div>
                          <div className="
                            sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 
                            text-right text-xs text-gray-400 dark:text-gray-400
                          ">
                            {hour === 0
                              ? '12AM'
                              : hour < 12
                                ? `${hour}AM`
                                : hour === 12
                                  ? '12PM'
                                  : `${hour - 12}PM`}
                          </div>
                        </div>
                        <div />
                      </React.Fragment>
                    ))}
                  </div>

                  <ol
                    className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 pointer-events-auto"
                    style={{ gridTemplateRows: '1.75rem repeat(288, minmax(0,1fr)) auto' }}
                  >
                    {renderDayEvents(daysArray as any[])}
                  </ol>
                </div>
              </div>
            </div>
            <div className="
              hidden w-1/2 max-w-md flex-none border-l border-gray-100 dark:border-gray-700 px-8 py-10 md:block
              bg-white dark:bg-gray-800
            ">
              <div className="flex items-center text-center text-gray-900 dark:text-gray-100 mb-4">
                <button
                  type="button"
                  className="
                    -m-1.5 flex flex-none items-center justify-center p-1.5 
                    text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-100
                  "
                  onClick={() => onDateChange(currentDate.subtract(1, 'month'))}
                >
                  <span className="sr-only">{t("Previous month")}</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                <div className="flex-auto text-sm font-semibold">{currentDate.format('MMMM YYYY')}</div>
                <button
                  type="button"
                  className="
                    -m-1.5 flex flex-none items-center justify-center p-1.5 
                    text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-100
                  "
                  onClick={() => onDateChange(currentDate.add(1, 'month'))}
                >
                  <span className="sr-only">{t("Next month")}</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div className="grid grid-cols-7 text-center text-xs text-gray-500 dark:text-gray-400 mb-2">
                <div>M</div><div>T</div><div>W</div><div>T</div><div>F</div><div>S</div><div>S</div>
              </div>
              <div className="
                isolate grid grid-cols-7 gap-px rounded-lg 
                bg-gray-200 dark:bg-gray-700 text-sm shadow ring-1 ring-gray-200 dark:ring-gray-600
              ">
                {[...Array(currentDate.daysInMonth())].map((_, i) => {
                  const d = currentDate.startOf('month').add(i, 'day');
                  const isToday = d.isSame(dayjs(), 'day');
                  const isSelected = d.isSame(currentDate, 'day');
                  const dayKey = d.format('YYYY-MM-DD');
                  const hasEvents = (eventsByDate[dayKey] || []).length > 0;
                  return (
                    <button
                      key={d.format('YYYY-MM-DD')}
                      type="button"
                      onClick={() => {
                        onDateChange(d);
                        onCalendarViewModeChange('day');
                      }}
                      className={classNames(
                        'py-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 focus:z-10',
                        'bg-white dark:bg-gray-800',
                        isToday && 'text-primary-500 font-semibold'
                      )}
                    >
                      <time
                        dateTime={d.format('YYYY-MM-DD')}
                        className={classNames(
                          'mx-auto flex items-center justify-center rounded-full size-7',
                          isSelected
                            ? 'bg-primary-500 text-white font-semibold'
                            : 'text-gray-900 dark:text-gray-100',
                        )}
                      >
                        {d.date()}
                      </time>
                      {hasEvents && (
                        <span className="mx-auto mt-0.5 block h-1 w-1 rounded-full bg-primary-400" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Day events list */}
              {currentDayEvents.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    {currentDate.format('dddd, MMMM D')}
                  </h3>
                  <ol className="space-y-2">
                    {currentDayEvents.map((event: any) => {
                      const colors = getEventColors(event.event_type);
                      return (
                        <li key={event.id}>
                          <button
                            onClick={() => onSelectEvent(event)}
                            className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors hover:opacity-80 ${colors.bg} ${colors.darkBg}`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
                              <p className={`text-sm font-medium truncate ${colors.text} ${colors.darkText}`}>
                                {event.title}
                              </p>
                            </div>
                            {event.start_time && (
                              <p className={`mt-1 text-xs ml-4 ${colors.text} ${colors.darkText} opacity-70`}>
                                {parseEventDate(event.start_time).format('h:mm A')}
                                {event.end_time && ` – ${parseEventDate(event.end_time).format('h:mm A')}`}
                              </p>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}
              {currentDayEvents.length === 0 && !isDayLoading && (
                <div className="mt-6 text-center">
                  <p className="text-xs text-gray-400 dark:text-gray-500">No events for this day</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarView;
